import React, { useEffect, useState } from 'react';
import { Button, StatusBadge } from '../common';
import type { StatusBadgeTone } from '../common';
import { ConversionReport, ReportReason, ReportStatus } from '../../types';

export const REASON_LABELS: Record<ReportReason, string> = {
  wrong_content: 'Wrong content read',
  poor_audio: 'Poor audio quality',
  truncated: 'Truncated / incomplete',
  other: 'Other',
  conversion_failed: 'Conversion failed',
};

export const STATUS_LABELS: Record<ReportStatus, string> = {
  open: 'Open',
  issue_raised: 'Issue raised',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

export const STATUS_TONES: Record<ReportStatus, StatusBadgeTone> = {
  open: 'live',
  issue_raised: 'warn',
  resolved: 'ok',
  dismissed: 'off',
};

export function formatReportDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export type ReportAction = 'listen' | 'issue' | 'refund' | 'notes' | 'status';

interface ReportDetailProps {
  report: ConversionReport;
  /** Which action is currently in flight, if any. */
  busy: ReportAction | null;
  error: string | null;
  isListening: boolean;
  onListen: () => void;
  onCreateIssue: () => void;
  onRefund: () => void;
  onSaveNotes: (notes: string) => void;
  onSetStatus: (status: ReportStatus) => void;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground">
        {label}
      </p>
      <div className="mt-1 font-body text-sm text-foreground break-words">{children}</div>
    </div>
  );
}

export default function ReportDetail({
  report,
  busy,
  error,
  isListening,
  onListen,
  onCreateIssue,
  onRefund,
  onSaveNotes,
  onSetStatus,
}: ReportDetailProps) {
  const [notes, setNotes] = useState(report.adminNotes ?? '');

  useEffect(() => {
    setNotes(report.adminNotes ?? '');
  }, [report.id, report.adminNotes]);

  const anyBusy = busy !== null;
  const notesDirty = notes !== (report.adminNotes ?? '');

  return (
    <section
      aria-label={`Report for ${report.conversionTitle || report.conversionId}`}
      className="bg-card border border-border rounded-sm p-6 space-y-6"
    >
      <header className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge tone={STATUS_TONES[report.status]}>
            {STATUS_LABELS[report.status]}
          </StatusBadge>
          {report.autoFlagged && <StatusBadge tone="error">Auto-flagged</StatusBadge>}
          {report.refunded && <StatusBadge tone="ok">Refunded</StatusBadge>}
          {report.githubIssueNumber != null && (
            <StatusBadge tone="warn">Issue #{report.githubIssueNumber}</StatusBadge>
          )}
        </div>
        <h2 className="font-display text-2xl font-medium text-foreground">
          {report.conversionTitle || 'Untitled edition'}
        </h2>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Reason">{REASON_LABELS[report.reason] ?? report.reason}</Field>
        <Field label="Reported">{formatReportDate(report.createdAt)}</Field>
        <Field label={report.autoFlagged ? 'Owner' : 'Reporter'}>
          {report.userEmail || report.userId}
        </Field>
        <Field label="Conversion">
          <code className="font-mono text-xs">{report.conversionId}</code>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Source">
            {report.sourceUrl && /^https?:/i.test(report.sourceUrl) ? (
              <a
                href={report.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 break-all"
              >
                {report.sourceUrl}
              </a>
            ) : (
              report.sourceUrl || 'Not available'
            )}
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field
            label={
              report.reason === 'conversion_failed' ? 'Pipeline error' : "User's description"
            }
          >
            {report.details ? (
              <blockquote className="border-l-2 border-border pl-3 whitespace-pre-wrap">
                {report.details}
              </blockquote>
            ) : (
              <span className="text-muted-foreground italic">No details provided.</span>
            )}
          </Field>
        </div>
        {report.reviewedBy && (
          <div className="sm:col-span-2">
            <Field label="Last reviewed by">{report.reviewedBy}</Field>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
        <Button
          variant="primary"
          size="sm"
          onClick={onListen}
          loading={busy === 'listen'}
          disabled={anyBusy || isListening}
        >
          {isListening ? 'Playing' : 'Listen'}
        </Button>

        {report.githubIssueUrl ? (
          <a
            href={report.githubIssueUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-3 py-1.5 font-mono lowercase tracking-[0.08em] text-xs rounded-sm border border-border text-foreground hover:bg-secondary"
          >
            View issue #{report.githubIssueNumber}
          </a>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={onCreateIssue}
            loading={busy === 'issue'}
            disabled={anyBusy}
          >
            Create GitHub issue
          </Button>
        )}

        <Button
          variant="danger"
          size="sm"
          onClick={onRefund}
          loading={busy === 'refund'}
          disabled={anyBusy || report.refunded}
        >
          {report.refunded ? 'Refunded' : 'Refund 1 credit'}
        </Button>

        <span className="flex-1" />

        {report.status !== 'resolved' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSetStatus('resolved')}
            loading={busy === 'status'}
            disabled={anyBusy}
          >
            Mark resolved
          </Button>
        )}
        {report.status !== 'dismissed' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSetStatus('dismissed')}
            loading={busy === 'status'}
            disabled={anyBusy}
          >
            Dismiss
          </Button>
        )}
        {(report.status === 'resolved' || report.status === 'dismissed') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onSetStatus('open')}
            loading={busy === 'status'}
            disabled={anyBusy}
          >
            Reopen
          </Button>
        )}
      </div>

      <div>
        <label
          htmlFor="admin-notes"
          className="block font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-2"
        >
          Admin notes
        </label>
        <textarea
          id="admin-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 4000))}
          rows={4}
          disabled={anyBusy}
          placeholder="What you heard, what you think went wrong, what to tell the user."
          className="w-full font-body text-sm bg-background border border-border rounded-sm px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60"
        />
        <div className="mt-2 flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onSaveNotes(notes)}
            loading={busy === 'notes'}
            disabled={anyBusy || !notesDirty}
          >
            Save notes
          </Button>
        </div>
      </div>

      {error && (
        <p role="alert" className="font-body text-sm text-destructive">
          {error}
        </p>
      )}
    </section>
  );
}
